import React, { useContext, useState, useEffect, useRef } from 'react';
import ReactSVG from 'react-svg';
import times from 'times.svg';
import minus from 'minus.svg';
import plus from 'plus.svg';
import useWindowSize from '../../hooks/useWindowSize.js';
import { MLSMapContext } from '../../context/MLSMapContext.js';
import formatNumber from '../../utils/formatNumber.js';
import mls_statuses, { otherStatuses } from '../../constants/mls_statuses';
import mls_amenities from '../../constants/mls_amenities.json';
import WindowQuery from '../../utils/WindowQuery.js';

const MAX_AREAS = 5;

export default function MLSFiltersDropdown({ areas }) {
  const updateQuery = new WindowQuery();

  const { width: windowWidth } = useWindowSize();
  const isMobile = windowWidth <= 768;

  const {
    showFilters,
    setShowFilters,
    bedrooms,
    setBedrooms,
    bathrooms,
    setBathrooms,
    minPrice,
    setMinPrice,
    maxPrice,
    setMaxPrice,
    minSqFt,
    setMinSqFt,
    maxSqFt,
    setMaxSqFt,
    clearFilters,
    minYearBuilt,
    setMinYearBuilt,
    maxYearBuilt,
    setMaxYearBuilt,
    minLotSize,
    setMinLotSize,
    maxLotSize,
    setMaxLotSize,
    city,
    setCity,
    amenities,
    setAmenities,
    filterStatuses,
    setFilterStatuses,
    mapAreas,
    setMapAreas,
    zipcode,
    setZipcode,
  } = useContext(MLSMapContext);

  const topOfDropdown = useRef();

  // Create Mins/Maxs for inputs
  const MIN_BEDROOMS = 0;
  const MAX_BEDROOMS = 10;
  const MIN_BATHROOMS = 0;
  const MAX_BATHROOMS = 5;

  // Create Local State (To use before applying changes)
  const [localBedrooms, setLocalBedrooms] = useState(bedrooms);
  const [localBathrooms, setLocalBathrooms] = useState(bathrooms);
  const [localMinPrice, setLocalMinPrice] = useState(minPrice);
  const [localMaxPrice, setLocalMaxPrice] = useState(maxPrice);
  const [localMinSqFt, setLocalMinSqFt] = useState(minSqFt);
  const [localMaxSqFt, setLocalMaxSqFt] = useState(maxSqFt);
  const [localMinYearBuilt, setLocalMinYearBuilt] = useState(minYearBuilt);
  const [localMaxYearBuilt, setLocalMaxYearBuilt] = useState(maxYearBuilt);
  const [localMinLotSize, setLocalMinLotSize] = useState(minLotSize);
  const [localMaxLotSize, setLocalMaxLotSize] = useState(maxLotSize);
  const [localFilterStatuses, setLocalFilterStatuses] = useState(
    filterStatuses
  );
  const [localAmenities, setLocalAmenities] = useState(amenities);
  const [localAreas, setLocalAreas] = useState(mapAreas);

  // Update local state when global state changes
  useEffect(() => {
    setLocalBedrooms(bedrooms);
    setLocalBathrooms(bathrooms);
    setLocalMinPrice(minPrice);
    setLocalMaxPrice(maxPrice);
    setLocalMinSqFt(minSqFt);
    setLocalMaxSqFt(maxSqFt);
    setLocalMinYearBuilt(minYearBuilt);
    setLocalMaxYearBuilt(maxYearBuilt);
    setLocalMinLotSize(minLotSize);
    setLocalMaxLotSize(maxLotSize);
    setLocalFilterStatuses(filterStatuses);
    setLocalAmenities(amenities);
    setLocalAreas(mapAreas);
  }, [
    bedrooms,
    bathrooms,
    minPrice,
    maxPrice,
    minSqFt,
    maxSqFt,
    minYearBuilt,
    maxYearBuilt,
    minLotSize,
    maxLotSize,
    amenities,
    filterStatuses,
    mapAreas,
  ]);

  // Reset local state when closing filters
  useEffect(() => {
    if (!showFilters) {
      setLocalBedrooms(bedrooms);
      setLocalBathrooms(bathrooms);
      setLocalMinPrice(minPrice);
      setLocalMaxPrice(maxPrice);
      setLocalMinSqFt(minSqFt);
      setLocalMaxSqFt(maxSqFt);
      setLocalMinYearBuilt(minYearBuilt);
      setLocalMaxYearBuilt(maxYearBuilt);
      setLocalMinLotSize(minLotSize);
      setLocalMaxLotSize(maxLotSize);
      setLocalFilterStatuses(filterStatuses);
      setLocalAmenities(amenities);
      setLocalAreas(mapAreas);
    }
  }, [
    amenities,
    bathrooms,
    bedrooms,
    filterStatuses,
    mapAreas,
    maxLotSize,
    maxPrice,
    maxSqFt,
    maxYearBuilt,
    minLotSize,
    minPrice,
    minSqFt,
    minYearBuilt,
    showFilters,
  ]);

  const handleApply = () => {
    setBedrooms(localBedrooms);
    setBathrooms(localBathrooms);
    setMinPrice(localMinPrice);
    setMaxPrice(localMaxPrice);
    setMinSqFt(localMinSqFt);
    setMaxSqFt(localMaxSqFt);
    setMinYearBuilt(localMinYearBuilt);
    setMaxYearBuilt(localMaxYearBuilt);
    setMinLotSize(localMinLotSize);
    setMaxLotSize(localMaxLotSize);
    setFilterStatuses(localFilterStatuses);
    setAmenities(localAmenities);
    setMapAreas(localAreas);

    setShowFilters(false);
  };

  const handleClear = () => {
    // Reset Local State
    setLocalBedrooms(0);
    setLocalBathrooms(0);
    setLocalMinPrice('');
    setLocalMaxPrice('');
    setLocalMinSqFt('');
    setLocalMaxSqFt('');
    setLocalMinYearBuilt('');
    setLocalMaxYearBuilt('');
    setLocalMinLotSize('');
    setLocalMaxLotSize('');
    setLocalFilterStatuses([]);
    setLocalAmenities([]);
    setLocalAreas([]);
    // Reset Context State
    setBedrooms(0);
    setBathrooms(0);
    setMinPrice('');
    setMaxPrice('');
    setMinSqFt('');
    setMaxSqFt('');
    setMinYearBuilt('');
    setMaxYearBuilt('');
    setMinLotSize('');
    setMaxLotSize('');
    setFilterStatuses([]);
    setAmenities([]);
    setMapAreas([]);
  };

  return (
    <div ref={topOfDropdown} className="filters-dropdown" hidden={!showFilters}>
      <section className={isMobile ? 'mobile-filter-buttons' : ''}>
        <button
          aria-label="Close Filter Dropdown"
          name="Close Filter Dropdown"
          type="button"
          className="close-filter-dropdown"
          onClick={() => setShowFilters(false)}
        >
          <ReactSVG src={times} />
        </button>
      </section>
      <section className="filter-dropdown-actions">
        {isMobile && (
          <button
            type="button"
            name="Clear All Filters"
            title="Clear All Filters"
            aria-label="Clear All Filters"
            className="btn btn--small btn--light"
            onClick={handleClear}
          >
            Clear All Filters
          </button>
        )}

        {city && isMobile && (
          <button
            type="button"
            name="Remove City Filter"
            aria-label="Remove City Filter"
            title="Remove City Filter"
            className="btn btn--small btn--light"
            onClick={() => {
              updateQuery.remove('city');
              setCity('');
            }}
          >
            Remove City
          </button>
        )}

        {zipcode && isMobile && (
          <button
            type="button"
            name="Remove Zipcode Filter"
            aria-label="Remove Zipcode Filter"
            title="Remove Zipcode Filter"
            className="btn btn--small btn--light"
            onClick={() => {
              updateQuery.remove('zipcode');
              setZipcode('');
            }}
          >
            Remove Zipcode
          </button>
        )}
      </section>

      <section className="filter-dropdown-section increment">
        <div>
          <h4 className="filter-dropdown-section-title">Bedrooms</h4>
          <section className="increment-inputs">
            <button
              aria-label="Subtract Bedrooms"
              name="Subtract Bedrooms"
              type="button"
              className={`btn btn--round subtract ${
                localBedrooms === MIN_BEDROOMS ? 'disabled' : ''
              }`}
              onClick={() => {
                setLocalBedrooms(Math.max(localBedrooms - 1, MIN_BEDROOMS));
              }}
            >
              <ReactSVG src={minus} />
            </button>
            <h3>{localBedrooms}+</h3>
            <button
              aria-label="Add Bedrooms"
              name="Add Bedrooms"
              type="button"
              className={`btn btn--round add ${
                localBedrooms === MAX_BEDROOMS ? 'disabled' : ''
              }`}
              onClick={() => {
                setLocalBedrooms(Math.min(localBedrooms + 1, MAX_BEDROOMS));
              }}
            >
              <ReactSVG src={plus} />
            </button>
          </section>
        </div>

        <div>
          <h4 className="filter-dropdown-section-title">Bathrooms</h4>
          <section className="increment-inputs">
            <button
              aria-label="Subtract bathrooms"
              name="Subtract bathrooms"
              type="button"
              className={`btn btn--round subtract ${false ? 'disabled' : ''}`}
              onClick={() => {
                setLocalBathrooms(Math.max(localBathrooms - 1, MIN_BATHROOMS));
              }}
            >
              <ReactSVG src={minus} />
            </button>
            <h3>{localBathrooms}+</h3>
            <button
              aria-label="Add Bathrooms"
              name="Add Bathrooms"
              type="button"
              className={`btn btn--round add ${false ? 'disabled' : ''}`}
              onClick={() => {
                setLocalBathrooms(Math.min(localBathrooms + 1, MAX_BATHROOMS));
              }}
            >
              <ReactSVG src={plus} />
            </button>
          </section>
        </div>
      </section>

      <section className="filter-dropdown-section">
        <h4 className="filter-dropdown-section-title">Price</h4>

        <div className="range-inputs-wrapper">
          <input
            type="text"
            aria-label="Min Price"
            name="Min Price"
            placeholder="Min"
            pattern="/^[0-9]/"
            value={localMinPrice}
            onChange={e => {
              setLocalMinPrice(e.target.value);
            }}
            onBlur={() => {
              const acceptedPrice = Math.max(
                Number(localMinPrice.replace(/,/g, '')),
                0
              );
              const formattedMinPrice =
                acceptedPrice && acceptedPrice != 0
                  ? formatNumber(acceptedPrice)
                  : '';
              setLocalMinPrice(formattedMinPrice);
            }}
          />
          <ReactSVG src={minus} className="range-inputs-divider" />
          <input
            type="text"
            aria-label="Max Price"
            name="Max Price"
            placeholder="Max"
            value={localMaxPrice}
            onChange={e => {
              setLocalMaxPrice(e.target.value);
            }}
            onBlur={() => {
              const acceptedPrice = Math.max(
                Number(localMaxPrice.replace(/,/g, '')),
                0
              );
              const formattedMaxPrice =
                acceptedPrice && acceptedPrice != 0
                  ? formatNumber(acceptedPrice)
                  : '';
              setLocalMaxPrice(formattedMaxPrice);
            }}
          />
        </div>
      </section>

      <section className="filter-dropdown-section checkbox">
        <h4 className="filter-dropdown-section-title">
          Search by Area (Select up to 5)
        </h4>

        <div className="filter-options areas">
          {areas.map((area, ind) => {
            const checked = localAreas.some(
              localArea => localArea.slug === area.slug
            );

            const disabled = localAreas.length >= MAX_AREAS && !checked;

            return (
              <div key={ind}>
                <label
                  htmlFor={`filters-type-${area.slug}`}
                  className={`${disabled ? 'disabled' : ''}`}
                >
                  <input
                    aria-label={`${area.name}`}
                    id={`filters-type-${area.slug}`}
                    type="checkbox"
                    disabled={disabled}
                    onChange={() => {
                      if (checked) {
                        const localAreasCopy = [...localAreas];
                        setLocalAreas(
                          localAreasCopy.filter(
                            localArea => localArea.slug !== area.slug
                          )
                        );
                      } else if (!disabled) {
                        setLocalAreas([...localAreas, area]);
                      }
                    }}
                    checked={checked}
                  />
                  <p>{area.name}</p>
                </label>
              </div>
            );
          })}
        </div>
      </section>

      <section className="filter-dropdown-section multi">
        <div className="filter-dropdown-section-inputs">
          <h4 className="filter-dropdown-section-title">Square Footage</h4>

          <div className="range-inputs-wrapper">
            <input
              type="text"
              aria-label="Min Square Feet"
              name="Min Square Feet"
              placeholder="Min"
              value={localMinSqFt}
              onChange={e => {
                setLocalMinSqFt(e.target.value);
              }}
              onBlur={() => {
                const acceptedNumber = Math.max(
                  Number(localMinSqFt.replace(/,/g, '')),
                  0
                );
                const formattedMinSqFt =
                  acceptedNumber && acceptedNumber != 0
                    ? formatNumber(acceptedNumber)
                    : '';
                // If min sqft is bigger than max sqft AND min/max sqft have a valid value, swap the values
                if (
                  Number(formattedMinSqFt.replace(/,/g, '')) >
                    Number(localMaxSqFt.replace(/,/g, '')) &&
                  (localMaxSqFt && formattedMinSqFt)
                ) {
                  setLocalMinSqFt(localMaxSqFt);
                  setLocalMaxSqFt(formattedMinSqFt);
                } else {
                  setLocalMinSqFt(formattedMinSqFt);
                }
              }}
            />
            <ReactSVG src={minus} className="range-inputs-divider" />
            <input
              type="text"
              aria-label="Max Square Feet"
              name="Max Square Feet"
              placeholder="Max"
              value={localMaxSqFt}
              onChange={e => {
                setLocalMaxSqFt(e.target.value);
              }}
              onBlur={() => {
                const acceptedNumber = Math.max(
                  Number(localMaxSqFt.replace(/,/g, '')),
                  0
                );
                const formattedMaxSqFt =
                  acceptedNumber && acceptedNumber != 0
                    ? formatNumber(acceptedNumber)
                    : '';
                // If max sqft is smaller than min sqft AND min/max sqft have a valid value, swap the values
                if (
                  Number(formattedMaxSqFt.replace(/,/g, '')) <
                    Number(localMinSqFt.replace(/,/g, '')) &&
                  (localMinSqFt && formattedMaxSqFt)
                ) {
                  setLocalMaxSqFt(localMinSqFt);
                  setLocalMinSqFt(formattedMaxSqFt);
                } else {
                  setLocalMaxSqFt(formattedMaxSqFt);
                }
              }}
            />
          </div>
        </div>

        <div className="filter-dropdown-section-inputs">
          <h4 className="filter-dropdown-section-title">Year Built</h4>

          <div className="range-inputs-wrapper">
            <input
              type="text"
              aria-label="Min Year Built"
              name="Min Year Built"
              placeholder="Min"
              value={localMinYearBuilt}
              onChange={e => {
                setLocalMinYearBuilt(e.target.value);
              }}
              onBlur={() => {
                if (
                  !parseInt(localMinYearBuilt) ||
                  parseInt(localMinYearBuilt) < 0
                ) {
                  setLocalMinYearBuilt('');
                }
              }}
            />
            <ReactSVG src={minus} className="range-inputs-divider" />
            <input
              type="text"
              aria-label="Max Year Built"
              name="Max Year Built"
              placeholder="Max"
              value={localMaxYearBuilt}
              onChange={e => {
                setLocalMaxYearBuilt(e.target.value);
              }}
              onBlur={() => {
                if (
                  !parseInt(localMaxYearBuilt) ||
                  parseInt(localMaxYearBuilt) < 0
                ) {
                  setLocalMaxYearBuilt('');
                }
              }}
            />
          </div>
        </div>

        <div className="filter-dropdown-section-inputs">
          <h4 className="filter-dropdown-section-title">Lot Size (ft.)</h4>

          <div className="range-inputs-wrapper">
            <input
              type="text"
              aria-label="Min Lot Size"
              name="Min Lot Size"
              placeholder="Min"
              value={localMinLotSize}
              onChange={e => {
                setLocalMinLotSize(e.target.value);
              }}
              onBlur={() => {
                const acceptedNumber = Math.max(
                  Number(localMinLotSize.replace(/,/g, '')),
                  0
                );
                const formattedMinLotSize =
                  acceptedNumber && acceptedNumber != 0
                    ? formatNumber(acceptedNumber)
                    : '';
                // If min sqft is bigger than max sqft AND min/max sqft have a valid value, swap the values
                if (
                  Number(formattedMinLotSize.replace(/,/g, '')) >
                    Number(localMaxLotSize.replace(/,/g, '')) &&
                  (localMaxLotSize && formattedMinLotSize)
                ) {
                  setLocalMinLotSize(localMaxLotSize);
                  setLocalMaxLotSize(formattedMinLotSize);
                } else {
                  setLocalMinLotSize(formattedMinLotSize);
                }
              }}
            />
            <ReactSVG src={minus} className="range-inputs-divider" />
            <input
              type="text"
              aria-label="Max Lot Size"
              name="Max Lot Size"
              placeholder="Max"
              value={localMaxLotSize}
              onChange={e => {
                setLocalMaxLotSize(e.target.value);
              }}
              onBlur={() => {
                const acceptedNumber = Math.max(
                  Number(localMaxLotSize.replace(/,/g, '')),
                  0
                );
                const formattedMaxLotSize =
                  acceptedNumber && acceptedNumber != 0
                    ? formatNumber(acceptedNumber)
                    : '';
                // If max sqft is smaller than min sqft AND min/max sqft have a valid value, swap the values
                if (
                  Number(formattedMaxLotSize.replace(/,/g, '')) <
                    Number(localMinLotSize.replace(/,/g, '')) &&
                  (localMinLotSize && formattedMaxLotSize)
                ) {
                  setLocalMaxLotSize(localMinLotSize);
                  setLocalMinLotSize(formattedMaxLotSize);
                } else {
                  setLocalMaxLotSize(formattedMaxLotSize);
                }
              }}
            />
          </div>
        </div>
      </section>

      <section className="filter-dropdown-section checkbox">
        <h4 className="filter-dropdown-section-title">Status (Select One)</h4>

        <div className="filter-options">
          {mls_statuses.map((status, ind) => {
            const checked = localFilterStatuses.some(
              localStatus => localStatus.label === status.label
            );
            return (
              <div key={ind}>
                <label htmlFor={`filters-type-${status.value}`}>
                  <input
                    aria-label={`${status.label}`}
                    id={`filters-type-${status.value}`}
                    type="checkbox"
                    onChange={() => {
                      if (checked) {
                        const localFilterStatusesCopy = [
                          ...localFilterStatuses,
                        ];
                        setLocalFilterStatuses(
                          localFilterStatusesCopy.filter(
                            localStatus => localStatus.label !== status.label
                          )
                        );
                      } else {
                        setLocalFilterStatuses([
                          ...localFilterStatuses,
                          status,
                        ]);
                      }
                    }}
                    checked={checked}
                  />
                  <p>{status.label}</p>
                </label>
              </div>
            );
          })}
        </div>

        <div aria-label="spacer" style={{ margin: '12px 0px' }} />

        <h4 className="filter-dropdown-section-title">Other</h4>

        <div className="filter-options">
          {otherStatuses.map((status, ind) => {
            const checked = localFilterStatuses.some(
              localStatus => localStatus.label === status.label
            );
            return (
              <div key={ind}>
                <label htmlFor={`filters-type-${status.value}`}>
                  <input
                    aria-label={`${status.label}`}
                    id={`filters-type-${status.value}`}
                    type="checkbox"
                    onChange={() => {
                      if (checked) {
                        const localFilterStatusesCopy = [
                          ...localFilterStatuses,
                        ];
                        setLocalFilterStatuses(
                          localFilterStatusesCopy.filter(
                            localStatus => localStatus.label !== status.label
                          )
                        );
                      } else {
                        setLocalFilterStatuses([
                          ...localFilterStatuses,
                          status,
                        ]);
                      }
                    }}
                    checked={checked}
                  />
                  <p>{status.label}</p>
                </label>
              </div>
            );
          })}
        </div>
      </section>

      <section className="filter-dropdown-section checkbox">
        <h4 className="filter-dropdown-section-title">Amenities</h4>

        <div className="filter-options">
          {mls_amenities.map((amenity, ind) => {
            const checked = localAmenities.some(
              localAmenity => localAmenity.label === amenity.label
            );

            return (
              <div key={ind}>
                <label htmlFor={`filters-type-${amenity.value}`}>
                  <input
                    aria-label={`${amenity.label}`}
                    id={`filters-type-${amenity.value}`}
                    type="checkbox"
                    onChange={() => {
                      if (checked) {
                        const localAmenitiesCopy = [...localAmenities];
                        setLocalAmenities(
                          localAmenitiesCopy.filter(
                            localAmenity => localAmenity.label !== amenity.label
                          )
                        );
                      } else {
                        setLocalAmenities([...localAmenities, amenity]);
                      }
                    }}
                    checked={checked}
                  />
                  <p>{amenity.label}</p>
                </label>
              </div>
            );
          })}
        </div>
      </section>

      <section className="dropdown-apply">
        <button
          aria-label="Apply Filters"
          name="Apply Filters"
          type="button"
          className="btn btn--dark Pbtn--small apply"
          onClick={handleApply}
        >
          Apply
        </button>
        <button
          aria-label="Clear Filters"
          name="Clear Filters"
          type="button"
          className="btn btn--small clear-filter"
          onClick={handleClear}
        >
          Clear
        </button>
      </section>
    </div>
  );
}
